import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { type Articulo, type ArticuloRequest, type ArticuloFiltered } from '../types/articulo';
import { ArticuloService } from '../services';

interface ArticuloState {
  // State
  articulos: Articulo[];
  filteredArticulos: ArticuloFiltered[];
  loading: boolean;
  error: string | null;
  selectedArticulo: Articulo | null;

  // UI State
  dialogOpen: boolean;

  // Actions
  fetchArticulos: () => Promise<void>;
  fetchFilteredArticulos: () => Promise<void>;
  createArticulo: (articuloRequest: ArticuloRequest) => Promise<void>;
  updateArticulo: (articuloRequest: ArticuloRequest) => Promise<void>;
  saveArticulo: (articuloRequest: ArticuloRequest) => Promise<void>;
  deleteArticulo: (idArticulo: number) => Promise<void>;
  changeEstado: (idArticulo: number, idEstado: number) => Promise<void>;

  // UI Actions
  setSelectedArticulo: (articulo: Articulo | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;

  // Reset
  reset: () => void;
}

const initialState = {
  articulos: [],
  filteredArticulos: [],
  loading: false,
  error: null,
  selectedArticulo: null,
  dialogOpen: false,
};

export const useArticuloStore = create<ArticuloState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchArticulos: async () => {
        set({ loading: true, error: null }, false, 'articulos/fetchArticulos/start');

        try {
          const articulos = await ArticuloService.getArticulos();
          set(
            { articulos, loading: false },
            false,
            'articulos/fetchArticulos/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch articulos',
            },
            false,
            'articulos/fetchArticulos/error'
          );
        }
      },

      fetchFilteredArticulos: async () => {
        set({ loading: true, error: null }, false, 'articulos/fetchFilteredArticulos/start');

        try {
          const filteredArticulos = await ArticuloService.getFilteredArticulos();
          set(
            { filteredArticulos, loading: false },
            false,
            'articulos/fetchFilteredArticulos/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch filtered articulos',
            },
            false,
            'articulos/fetchFilteredArticulos/error'
          );
        }
      },

      createArticulo: async (articuloRequest: ArticuloRequest) => {
        set({ loading: true, error: null }, false, 'articulos/createArticulo/start');

        try {
          const newArticulo = await ArticuloService.saveArticulo(articuloRequest);
          set(
            (state) => ({
              articulos: [...state.articulos, newArticulo],
              loading: false,
              dialogOpen: false,
              selectedArticulo: null,
            }),
            false,
            'articulos/createArticulo/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to create articulo',
            },
            false,
            'articulos/createArticulo/error'
          );
          throw error;
        }
      },

      updateArticulo: async (articuloRequest: ArticuloRequest) => {
        set({ loading: true, error: null }, false, 'articulos/updateArticulo/start');

        try {
          const updatedArticulo = await ArticuloService.saveArticulo(articuloRequest);
          set(
            (state) => ({
              articulos: state.articulos.map((articulo) =>
                articulo.idArticulo === updatedArticulo.idArticulo ? updatedArticulo : articulo
              ),
              loading: false,
              dialogOpen: false,
              selectedArticulo: null,
            }),
            false,
            'articulos/updateArticulo/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to update articulo',
            },
            false,
            'articulos/updateArticulo/error'
          );
          throw error;
        }
      },

      saveArticulo: async (articuloRequest: ArticuloRequest) => {
        const isUpdate = !!articuloRequest.idArticulo;
        if (isUpdate) {
          await get().updateArticulo(articuloRequest);
        } else {
          await get().createArticulo(articuloRequest);
        }
      },

      deleteArticulo: async (idArticulo: number) => {
        set({ loading: true, error: null }, false, 'articulos/deleteArticulo/start');

        try {
          // Note: Implement delete endpoint when available
          // await ArticuloService.deleteArticulo(idArticulo);
          set(
            (state) => ({
              articulos: state.articulos.filter((articulo) => articulo.idArticulo !== idArticulo),
              loading: false,
            }),
            false,
            'articulos/deleteArticulo/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to delete articulo',
            },
            false,
            'articulos/deleteArticulo/error'
          );
        }
      },

      changeEstado: async (idArticulo: number, idEstado: number) => {
        try {
          const updated = await ArticuloService.changeEstado(idArticulo, idEstado);
          set(
            (state) => ({
              articulos: state.articulos.map((articulo) =>
                articulo.idArticulo === idArticulo ? { ...articulo, ...updated } : articulo
              ),
            }),
            false,
            'articulos/changeEstado/success'
          );
        } catch (error) {
          set(
            {
              error: error instanceof Error ? error.message : 'Failed to change estado',
            },
            false,
            'articulos/changeEstado/error'
          );
        }
      },

      // UI Actions
      setSelectedArticulo: (articulo: Articulo | null) => {
        set({ selectedArticulo: articulo }, false, 'articulos/setSelectedArticulo');
      },

      openDialog: () => {
        set({ dialogOpen: true }, false, 'articulos/openDialog');
      },

      closeDialog: () => {
        set({ dialogOpen: false, selectedArticulo: null }, false, 'articulos/closeDialog');
      },

      clearError: () => {
        set({ error: null }, false, 'articulos/clearError');
      },

      // Reset
      reset: () => {
        set(initialState, false, 'articulos/reset');
      },
    }),
    {
      name: 'articulo-store',
    }
  )
);